import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Share2, Copy, Trash2, Plus, ExternalLink, ShieldCheck } from "lucide-react";

export default function ShareManager() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shares");
      setShares(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buildUrl = (token) => `${window.location.origin}/share/${token}`;

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/shares");
      toast.success("New share link created");
      await load();
    } catch {
      toast.error("Could not create share link");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(buildUrl(token));
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this share link? Anyone with it will lose access.")) return;
    try {
      await api.delete(`/shares/${id}`);
      toast.success("Share link revoked");
      await load();
    } catch {
      toast.error("Could not revoke");
    }
  };

  const active = shares.filter((s) => !s.revoked);
  const revoked = shares.filter((s) => s.revoked);

  return (
    <div className="neu-card p-6 md:p-8" data-testid="share-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2">
          <Share2 className="w-5 h-5 text-neu-accent" />
          <div className="heading text-lg text-[#3F4E5B]">Coordinator share link</div>
        </div>
        <button
          onClick={create}
          disabled={creating}
          className="btn-accent inline-flex items-center gap-2"
          data-testid="share-create"
        >
          <Plus className="w-4 h-4" /> {creating ? "Creating…" : "New link"}
        </button>
      </div>
      <p className="text-sm subtle mb-6 max-w-2xl">
        Generate a private read-only link to share your case log with your program director or coordinator.
        They can view analytics and cases — no login required. Revoke at any time.
      </p>

      {loading ? (
        <div className="text-sm subtle">Loading…</div>
      ) : active.length === 0 && revoked.length === 0 ? (
        <div className="text-center py-8 neu-inset">
          <ShieldCheck className="w-10 h-10 text-neu-muted mx-auto mb-3" />
          <div className="text-sm subtle">No share links yet. Create one to send to your coordinator.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {active.map((s) => {
            const url = buildUrl(s.token);
            return (
              <div key={s.id} className="neu-inset p-4 flex items-center gap-3 flex-wrap" data-testid={`share-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs subtle uppercase">Active share link</div>
                  <div className="font-mono text-xs md:text-sm text-neu-text truncate mt-1" title={url}>
                    {url}
                  </div>
                  <div className="text-xs subtle mt-1">
                    Created {new Date(s.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copy(s.token)}
                    className="btn-neu inline-flex items-center gap-2"
                    data-testid={`share-copy-${s.id}`}
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-neu inline-flex items-center gap-2"
                    data-testid={`share-open-${s.id}`}
                  >
                    <ExternalLink className="w-4 h-4" /> Open
                  </a>
                  <button
                    onClick={() => revoke(s.id)}
                    className="btn-neu inline-flex items-center gap-2 hover:text-red-600"
                    data-testid={`share-revoke-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" /> Revoke
                  </button>
                </div>
              </div>
            );
          })}

          {revoked.length > 0 && (
            <details className="mt-4">
              <summary className="text-sm subtle cursor-pointer">
                {revoked.length} revoked link{revoked.length > 1 ? "s" : ""}
              </summary>
              <div className="mt-3 space-y-2">
                {revoked.map((s) => (
                  <div key={s.id} className="neu-inset p-3 opacity-60">
                    <div className="font-mono text-xs truncate">{buildUrl(s.token)}</div>
                    <div className="text-xs subtle">Revoked</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
